import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Send, Sparkles, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const SUGGESTIONS = [
  "How much grey cement do I have in stock?",
  "Show me last week's dispatches to Sharma Ji.",
  "Is mahine ka profit kitna hua?",
  "Which product sold most this month?",
  "Kaunsa material sabse jyada consume ho raha hai?",
  "What's my current gross margin?",
];

// Very lightweight markdown → HTML: **bold**, line breaks, bullets.
// All rendered HTML is sanitized via DOMPurify (strips <script>, event handlers, etc.)
const SANITIZE_OPTS = { ALLOWED_TAGS: ["strong", "em", "code"], ALLOWED_ATTR: ["class"] };
const safe = (html) => DOMPurify.sanitize(html, SANITIZE_OPTS);

function renderRich(text) {
  const lines = (text || "").split("\n");
  return lines.map((ln, i) => {
    // bullet
    const bullet = ln.match(/^\s*[-•]\s+(.*)/);
    const html = (bullet ? bullet[1] : ln)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#1C1C1A]">$1</strong>')
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, '<code class="text-[11px] bg-[#F1F1EF] px-1 rounded">$1</code>');
    if (bullet) {
      return <div key={i} className="flex gap-2 my-0.5"><span className="text-[#C85A42]">•</span><span dangerouslySetInnerHTML={{ __html: safe(html) }} /></div>;
    }
    if (!ln.trim()) return <div key={i} className="h-2" />;
    return <div key={i} className="my-0.5" dangerouslySetInnerHTML={{ __html: safe(html) }} />;
  });
}

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message: q });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't process that. Please try again." }]);
    } finally { setBusy(false); }
  };

  const clear = () => setMessages([]);

  return (
    <div className="h-screen flex flex-col bg-[#F9F9F8]">
      <div className="p-8 pb-4">
        <div className="max-w-3xl mx-auto flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A] flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[#C85A42]" /> Powered by Claude
            </div>
            <h1 className="font-display text-4xl tracking-tighter font-black">Chat with Sango AI</h1>
            <p className="text-[#6E6E6A] text-sm mt-1">Ask anything about your factory data — English, Hindi, or Hinglish.</p>
          </div>
          {messages.length > 0 && <Button variant="ghost" size="sm" onClick={clear} data-testid="chat-clear">Clear</Button>}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 pb-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.length === 0 && (
            <div className="mt-8">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A] mb-3">Try asking</div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)} data-testid={`suggest-${i}`}
                    className="text-xs px-3 py-2 rounded-full border border-[#D8D8D5] bg-white hover:bg-[#1C1C1A] hover:text-white transition-colors text-left">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 rise ${msg.role === "user" ? "justify-end" : ""}`}>
              {msg.role === "assistant" && (
                <div className="w-8 h-8 shrink-0 rounded-full bg-[#1C1C1A] text-white flex items-center justify-center">
                  <Sparkles className="w-4 h-4" />
                </div>
              )}
              <Card className={`max-w-[80%] p-4 shadow-none rounded-md border ${msg.role === "user" ? "bg-[#1C1C1A] text-white border-[#1C1C1A]" : "bg-white border-[#D8D8D5]"}`}>
                <div className={`text-sm ${msg.role === "user" ? "text-white" : "text-[#1C1C1A]"}`}>
                  {msg.role === "assistant" ? renderRich(msg.content) : msg.content}
                </div>
              </Card>
              {msg.role === "user" && (
                <div className="w-8 h-8 shrink-0 rounded-full bg-[#E8E8E6] text-[#1C1C1A] flex items-center justify-center">
                  <UserIcon className="w-4 h-4" />
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="flex gap-3">
              <div className="w-8 h-8 shrink-0 rounded-full bg-[#1C1C1A] text-white flex items-center justify-center">
                <Sparkles className="w-4 h-4 animate-pulse" />
              </div>
              <Card className="p-4 border border-[#D8D8D5] shadow-none rounded-md bg-white">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C85A42] animate-bounce" style={{ animationDelay: "0s" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C85A42] animate-bounce" style={{ animationDelay: "0.15s" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C85A42] animate-bounce" style={{ animationDelay: "0.3s" }} />
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      <div className="p-8 pt-4 border-t border-[#D8D8D5] bg-white">
        <div className="max-w-3xl mx-auto flex gap-2 items-end">
          <Textarea
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Ask ${user?.name?.split(" ")[0] || "away"}… (Shift+Enter for new line)`}
            className="min-h-[48px] max-h-40 resize-none flex-1 focus-visible:ring-[#1C1C1A]"
            data-testid="chat-input"
          />
          <Button onClick={() => send()} disabled={busy || !input.trim()} data-testid="chat-send" className="h-12 px-5 bg-[#1C1C1A] hover:bg-black text-white gap-2">
            <Send className="w-4 h-4" /> Send
          </Button>
        </div>
      </div>
    </div>
  );
}
